import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { MeetingItem, MeetingSettingsPush, SiStatus, WaitingRoomActiveType } from '@tencent/wemeet-websdk-next';
import { t } from 'i18next';
import { getLocalStorageMediaSetting } from '@services/storage';
import { AppState } from '..';
import { getMeetingCodeFromUrl } from '@utils/url';
import { fetchInviteContent } from './metting.trunk';

/**
 * @description 会议相关内容
 */
export interface MeetingStoreState {
  /**
   * 进会所需要的信息
   *
   * 1. 用户在进会表格填写的信息
   * 2. 用户在浏览器直接打开 /r/${meetingCode} 页面
   */
  meetingInfoInput: {
    meetingCode: string;
    nickname: string;
    password: string;
    enableAudio: boolean;
    enableVideo: boolean;
  };

  // 进会前查询的会议信息
  meetingInfo: MeetingItem;

  /** 会议中的会议设置 */
  meetingSettings: MeetingSettingsPush;
  meetingCheckResult: boolean;

  ifInMeeting: boolean;
  waitingRoomActiveType: WaitingRoomActiveType;
  isHostInMeeting: boolean;
  /** @description 邀请弹框内容 */
  inviteContent: string;
  /** @description 网络状态  */
  networkState: {
    currentlinkQuality: string;
    level: number;
    rtt: string;
    // 上行丢包率
    uplinkLoss: number;
    // 下行丢包率
    downlinkLoss: number;
  };
  si_status: SiStatus;
}

const initialState: MeetingStoreState = {
  /**
   * 会议信息
   *
   * 进会前所能获取的会议相关的信息
   * 数据来源：
   * 1. 用户输入： 表单、地址栏
   * 2. localStorage 记忆的用户媒体相关选项
   * 3. 密码：用于等候室不需要再重复登录
   * 4.
   *
   * important: 进会后请使用 meetingInfo 获取会议信息
   */
  meetingInfoInput: {
    meetingCode: getMeetingCodeFromUrl(),
    nickname: '',
    password: '',
    enableAudio: getLocalStorageMediaSetting().enableAudio,
    enableVideo: getLocalStorageMediaSetting().enableVideo,
  },

  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  meetingInfo: {} as MeetingItem,
  meetingCheckResult: false,
  meetingSettings: {},

  ifInMeeting: false,
  waitingRoomActiveType: WaitingRoomActiveType.IN_WAITING_ROOM,
  isHostInMeeting: false,
  inviteContent: '',
  networkState: {
    currentlinkQuality: t('未知'),
    rtt: '0ms',
    uplinkLoss: 0,
    downlinkLoss: 0,
    level: 1,
  },
  si_status: SiStatus.NOT_START,
};

export const meetingSlice = createSlice({
  name: 'meeting',
  initialState,
  reducers: {
    setMeetingInfoInput: (state, action: PayloadAction<Partial<MeetingStoreState['meetingInfoInput']>>) => {
      state.meetingInfoInput = {
        ...state.meetingInfoInput,
        ...action.payload,
      };
    },
    setIfInMeeting: (state, action) => {
      state.ifInMeeting = action.payload;
    },
    setMeetingInfo: (state, action) => {
      state.meetingInfo = action.payload;
    },
    setMeetingSettings: (state, action) => {
      // console.log("+==:", { ...state.meetingSettings, ...action.payload })
      state.meetingSettings = { ...state.meetingSettings, ...action.payload };
    },
    setWaitingRoomActiveType: (state, action) => {
      state.waitingRoomActiveType = action.payload;
    },
    setIsHostInMeeting: (state, action) => {
      state.isHostInMeeting = action.payload;
    },
    setNetWorkState: (state, action) => {
      // 合并对象，过来的时候数据可能不全
      state.networkState = {
        ...state.networkState,
        ...action.payload,
      };
    },
    setSiStatus: (state, action) => {
      state.si_status = action.payload;
    },
    setMeetingCheckResult: (state, action) => {
      state.si_status = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchInviteContent.fulfilled, (state, action) => {
      state.inviteContent = action.payload;
    });
  },
});

export const {
  setMeetingInfoInput,
  setIfInMeeting,
  setMeetingInfo,
  setWaitingRoomActiveType,
  setIsHostInMeeting,
  setNetWorkState,
  setSiStatus,
  setMeetingSettings,
  setMeetingCheckResult,
} = meetingSlice.actions;

// getter
export const getIfInMeeting = (state: AppState) => state.meeting.ifInMeeting;
export const getMeetingInfoInput = (state: AppState) => state.meeting.meetingInfoInput;
/**
 * 查询会议信息
 */
export const getMeetingInfo = (state: AppState) => state.meeting.meetingInfo;

/**
 * 查询会议设置
 */
export const getMeetingSettings = (state: AppState) => state.meeting.meetingSettings;

/** 查询等候室状态 */
export const getWaitingRoomActiveType = (state: AppState) => state.meeting.waitingRoomActiveType;

/** 查询主持人是否在会议中 */
export const getIsHostInMeeting = (state: AppState) => state.meeting.isHostInMeeting;

/** 查询邀请信息 */
export const getInviteContent = (state: AppState) => state.meeting.inviteContent;

/** 查询网络状态 */
export const getNetWorkState = (state: AppState) => state.meeting.networkState;

/** 查询网络状态 */
export const getSiStatus = (state: AppState) => state.meeting.si_status;

export default meetingSlice.reducer;
